import theme from '@/config/theme';
import React from 'react';
import { TextInput, TextInputProps } from 'react-native';
import { Platform } from 'react-native';
import { getLogger } from '@/utils/logger';

export type FormattedInputType = 'cnpj' | 'phone' | 'cep' | 'number';

interface FormattedInputProps extends Omit<TextInputProps, 'onChangeText' | 'value'> {
  value: string;
  onChangeText: (text: string) => void;
  type: FormattedInputType;
}

const NumberInputLogger = getLogger('FORMATTED-INPUT', 'NUMBER');

// Função para formatar CNPJ
function formatCNPJ(value: string): string {
  // Remove tudo que não for número
  const digits = value.replace(/\D/g, '');
  // Aplica a máscara: 00.000.000/0000-00
  let formatted = digits;
  if (formatted.length > 2) formatted = formatted.replace(/(\d{2})(\d)/, '$1.$2');
  if (formatted.length > 6) formatted = formatted.replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
  if (formatted.length > 10) formatted = formatted.replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4');
  if (formatted.length > 15) formatted = formatted.replace(/(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
  return formatted.slice(0, 18); // Limita ao tamanho máximo do CNPJ formatado
}

// Função para formatar telefone brasileiro
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    // Fixo: (99) 9999-9999
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6, 10)}`.replace(/-$/, '');
  }
  // Celular: (99) 99999-9999
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`.replace(/-$/, '');
}

// Função para formatar CEP brasileiro
function formatCEP(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`.replace(/-$/, '');
}

// Função para formatar números com 2 casas decimais, vírgula e separador de milhar.
// Ex.: "1" => "0,01"; "1000" => "10,00"; "123456" => "1.234,56".
function formatNumber(value: string): string {
  // Extrai somente dígitos
  const raw = value.replace(/\D/g, '');

  if (!raw) return '';

  // Remove zeros à esquerda, mantendo pelo menos um dígito
  let digits = raw.replace(/^0+/, '');
  if (digits.length === 0) digits = '0';

  if (digits.length === 1) {
    // 5 -> 0,05
    return `0,0${digits}`;
  }
  if (digits.length === 2) {
    // 50 -> 0,50
    return `0,${digits}`;
  }

  const intPart = digits.slice(0, -2);
  const decPart = digits.slice(-2);

  const intWithThousand = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${intWithThousand},${decPart}`;
}

const FormattedInput: React.FC<FormattedInputProps> = ({ value, onChangeText, type, ...rest }) => {
  // Handler para aplicar a máscara
  const handleChange = (text: string) => {
    const start = Date.now();
    let formatted = text;
    if (type === 'cnpj') {
      formatted = formatCNPJ(text);
    } else if (type === 'phone') {
      formatted = formatPhone(text);
    } else if (type === 'cep') {
      formatted = formatCEP(text);
    } else if (type === 'number') {
      formatted = formatNumber(text);
    }
    const delta = Date.now() - start;
    if (type === 'number') {
      NumberInputLogger.info(`[${Platform.OS}] handleChange duration: ${delta} ms | raw: "${text}" -> formatted: "${formatted}"`);
    }
    onChangeText(formatted);
  };

  return (
    <TextInput
      {...rest}
      value={value}
      onChangeText={handleChange}
      keyboardType="numeric"
      maxLength={
        type === 'phone'
          ? 15
          : type === 'cep'
          ? 9
          : type === 'cnpj'
          ? 18
          : undefined
      } // Limites específicos por tipo, genérico para número
      placeholder={rest.placeholder || 'Digite apenas números'}
      placeholderTextColor={theme.colors.text.tertiary}
      autoCapitalize="none"
    />
  );
};

export default FormattedInput; 